import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { syncAPI, accountsAPI, oauthAPI } from '../services/api';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  ArrowPathIcon,
  PhotoIcon,
  PlayIcon,
  XMarkIcon,
  XCircleIcon,
  UserCircleIcon,
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const BackupProgress = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [retryingPosts, setRetryingPosts] = useState(new Set());
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 20;

  // Get OAuth status
  const { data: oauthStatus } = useQuery({
    queryKey: ['oauth-status'],
    queryFn: () => oauthAPI.getOAuthStatus().then(res => res.data),
  });

  // Get Instagram accounts for pro/agency users
  const { data: accountsData } = useQuery({
    queryKey: ['instagram-accounts'],
    queryFn: () => accountsAPI.getInstagramAccounts().then(res => res.data),
    enabled: oauthStatus?.instagram_connected && ['pro', 'agency'].includes(user?.subscription_tier),
  });

  // Fetch backups with pagination
  const { data: backupsData, isLoading, refetch } = useQuery({
    queryKey: ['backups', filter, selectedAccountId, currentPage],
    queryFn: () => {
      const params = {
        limit: ITEMS_PER_PAGE,
        offset: (currentPage - 1) * ITEMS_PER_PAGE,
      };
      if (filter !== 'all') {
        params.status_filter = filter;
      }
      if (selectedAccountId) {
        params.account_id = selectedAccountId;
      }
      return syncAPI.getBackups(params).then(res => res.data);
    },
    refetchInterval: 5000, // Auto-refresh every 5 seconds
    keepPreviousData: true, // Keep previous data while loading new page
  });
  
  // Handle both old and new API response formats
  const backups = backupsData?.backups || backupsData || [];
  const totalBackups = backupsData?.total || backups.length;
  const totalPages = Math.ceil(totalBackups / ITEMS_PER_PAGE);

  // Reset page when filter or account changes
  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    setCurrentPage(1);
  };

  const handleAccountChange = (accountId) => {
    setSelectedAccountId(accountId);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch only running sync job
  const { data: runningSyncJob, isLoading: syncJobLoading } = useQuery({
    queryKey: ['running-sync-job'],
    queryFn: async () => {
      const response = await syncAPI.getSyncJobs(1);
      const jobs = response.data;
      // Find the first running job
      return jobs.find(job => job.status === 'running') || null;
    },
    refetchInterval: 2000, // Auto-refresh every 2 seconds for real-time updates
    enabled: true,
  });

  // Fetch backup folder URL for Google Drive access
  const { data: backupFolderData } = useQuery({
    queryKey: ['backup-folder-url'],
    queryFn: () => syncAPI.getBackupFolderUrl().then(res => res.data),
    staleTime: 60000, // Cache for 1 minute
  });

  const handleRetryBackup = async (postId) => {
    setRetryingPosts(prev => new Set(prev).add(postId));
    try {
      await syncAPI.retryBackup(postId);
      toast.success(t('backups.retryStarted'));
      setTimeout(() => refetch(), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('backups.retryFailed'));
    } finally {
      setRetryingPosts(prev => {
        const newSet = new Set(prev);
        newSet.delete(postId);
        return newSet;
      });
    }
  };

  const handleRetryAllFailed = async () => {
    const failedBackups = backups?.filter(backup => backup.status === 'failed' && backup.instagram_post_id) || [];
    
    if (failedBackups.length === 0) {
      toast.info(t('backups.noFailedBackups'));
      return;
    }

    toast.info(t('backups.retrying', { count: failedBackups.length }));

    // Add all failed posts to retrying set
    setRetryingPosts(prev => {
      const newSet = new Set(prev);
      failedBackups.forEach(backup => newSet.add(backup.instagram_post_id));
      return newSet;
    });

    // Retry all failed backups
    const retryPromises = failedBackups.map(backup => 
      syncAPI.retryBackup(backup.instagram_post_id).catch(error => {
        console.error(`Failed to retry backup ${backup.instagram_post_id}:`, error);
        return error;
      })
    );

    try {
      await Promise.all(retryPromises);
      toast.success(t('backups.retryStartedFor', { count: failedBackups.length }));
      setTimeout(() => refetch(), 2000);
    } catch (error) {
      toast.error(t('backups.someRetriesFailed'));
    } finally {
      // Remove all from retrying set
      setRetryingPosts(prev => {
        const newSet = new Set(prev);
        failedBackups.forEach(backup => newSet.delete(backup.instagram_post_id));
        return newSet;
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon className="h-5 w-5 text-green-400" />;
      case 'failed':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />;
      case 'processing':
        return <ArrowPathIcon className="h-5 w-5 text-blue-400 animate-spin" />;
      default:
        return <ClockIcon className="h-5 w-5 text-yellow-400" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-400/20 text-green-300 border border-green-400/30';
      case 'failed':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-400/20 text-red-300 border border-red-400/30';
      case 'processing':
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-400/20 text-blue-300 border border-blue-400/30';
      default:
        return 'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-400/20 text-yellow-300 border border-yellow-400/30';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatBytes = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Check if media type is video
  const isVideo = (mediaType) => {
    return mediaType && mediaType.toLowerCase().includes('video');
  };

  // Handle media click - videos open in modal, photos open in new tab
  const handleMediaClick = (post) => {
    if (!post || !post.media_url) return;
    
    if (isVideo(post.media_type)) {
      setSelectedVideo(post);
    } else {
      window.open(post.media_url, '_blank', 'noopener,noreferrer');
    }
  };

  // Check if backup is a carousel (has multiple items)
  const isCarouselBackup = (backup) => {
    return backup.error_message && 
           backup.error_message.toLowerCase().includes('carousel') &&
           backup.error_message.toLowerCase().includes('items backed up');
  };

  // Handle viewing backup in Google Drive
  const handleViewBackup = (backup, event) => {
    // Prevent any default behavior and stop propagation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // For carousel backups, open the folder containing all items
    if (isCarouselBackup(backup) && backup.google_drive_folder_id) {
      const folderUrl = `https://drive.google.com/drive/folders/${backup.google_drive_folder_id}`;
      window.open(folderUrl, '_blank', 'noopener,noreferrer');
    } else if (backup.google_drive_file_id) {
      const driveUrl = `https://drive.google.com/file/d/${backup.google_drive_file_id}/view`;
      window.open(driveUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-400 mx-auto"></div>
          <p className="mt-4 text-gray-300">Loading backups...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-white">{t('backups.title')}</h1>
                <p className="text-gray-300 mt-2">{t('backups.subtitle')}</p>
                <div className="mt-4 inline-flex items-center space-x-1 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1 text-white text-sm">
                  <ArrowPathIcon className="h-4 w-4 text-blue-400" />
                  <span>{t('backups.autoRefreshing')}</span>
                </div>
              </div>
              
              {/* Open Google Drive Folder Button */}
              {backupFolderData?.folder_url && (
                <a
                  href={backupFolderData.folder_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-5 py-3 text-sm font-semibold rounded-xl text-white bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                >
                  <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.71 3.5L1.15 15l3.42 5.96h16.86L24.85 15l-6.56-11.5H7.71zm.59 1h9.4l5.72 10-2.86 5H3.44l-2.86-5 5.72-10z"/>
                    <path d="M12 8.5L7.5 16h9L12 8.5zm0 2.31l2.25 3.94h-4.5L12 10.81z"/>
                  </svg>
                  {t('backups.openInDrive')}
                  <ArrowTopRightOnSquareIcon className="h-4 w-4 ml-2" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Current Sync Status */}
        {runningSyncJob && (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl mb-8">
            <h2 className="text-xl font-semibold text-white mb-6">{t('backups.currentSyncStatus')}</h2>
            
            {syncJobLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400"></div>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const job = runningSyncJob;
                const isCompleted = job.status === 'completed';
                const isFailed = job.status === 'failed';
                const isRunning = job.status === 'running';
                const isCancelled = job.status === 'cancelled';
                
                // Calculate progress safely
                const progress = job.posts_total > 0 
                  ? Math.round((job.posts_processed / job.posts_total) * 100) 
                  : 0;
                
                // Determine status message and check for free tier limit
                let statusMessage = '';
                const isFreeTierLimit = job.error_message && job.error_message.includes('Free tier limit');
                let remainingUnbacked = null;
                
                if (isCompleted) {
                  // Check if there's a limit message for free users
                  if (isFreeTierLimit) {
                    // Parse the error message to extract numbers
                    const match = job.error_message.match(/(\d+)\s+of\s+(\d+)\s+posts/);
                    if (match) {
                      const backedUp = parseInt(match[1]);
                      const total = parseInt(match[2]);
                      remainingUnbacked = total - backedUp;
                      statusMessage = t('backups.syncStatus.freeTierLimit', { backedUp, total });
                    } else {
                      statusMessage = job.error_message;
                    }
                  } else {
                    statusMessage = job.posts_processed > 0 
                      ? t('backups.syncStatus.processed', { count: job.posts_processed })
                      : t('backups.syncStatus.noNewPosts');
                  }
                } else if (isFailed) {
                  statusMessage = job.error_message || t('backups.syncStatus.failed');
                } else if (isRunning) {
                  statusMessage = job.posts_total > 0 
                    ? t('backups.syncStatus.processing', { processed: job.posts_processed, total: job.posts_total })
                    : t('backups.syncStatus.starting');
                } else if (isCancelled) {
                  statusMessage = t('backups.syncStatus.cancelled');
                }
                
                return (
                  <div 
                    key={job.id} 
                    className={`p-6 bg-white/5 backdrop-blur-sm rounded-2xl border ${
                      isCompleted ? (isFreeTierLimit ? 'border-yellow-400/30' : 'border-green-400/30') : 
                      isFailed ? 'border-red-400/30' : 
                      isRunning ? 'border-yellow-400/30' : 
                      'border-gray-400/30'
                    }`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center">
                        {isCompleted ? (
                          isFreeTierLimit ? (
                            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400 mr-3" />
                          ) : (
                            <CheckCircleIcon className="h-6 w-6 text-green-400 mr-3" />
                          )
                        ) : isFailed ? (
                          <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mr-3" />
                        ) : isRunning ? (
                          <ArrowPathIcon className="h-6 w-6 text-yellow-400 mr-3 animate-spin" />
                        ) : (
                          <XCircleIcon className="h-6 w-6 text-gray-400 mr-3" />
                        )}
                        <div>
                          <h3 className="font-semibold text-white">
                            {job.job_type === 'scheduled_sync' ? 'Automatic Sync' : 'Manual Sync'}
                          </h3>
                          <p className="text-sm text-gray-300 mt-1 break-words max-w-3xl">{statusMessage}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        isCompleted ? (isFreeTierLimit ? 'bg-yellow-400/20 text-yellow-400' : 'bg-green-400/20 text-green-400') :
                        isFailed ? 'bg-red-400/20 text-red-400' :
                        isRunning ? 'bg-yellow-400/20 text-yellow-400' :
                        'bg-gray-400/20 text-gray-400'
                      }`}>
                        {job.status}
                      </span>
                    </div>
                    
                    {/* Show upgrade prompt for free tier limit */}
                    {isCompleted && isFreeTierLimit && user?.subscription_tier === 'free' && remainingUnbacked > 0 && (
                      <div className="mb-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 rounded-xl p-4 border border-yellow-500/30">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-white mb-1">
                              Free tier limit reached
                            </p>
                            <p className="text-sm text-gray-200 mb-3">
                              {remainingUnbacked} {remainingUnbacked === 1 ? 'post' : 'posts'} remaining unbacked up. Upgrade to Premium or Pro to backup all your posts and get automatic backups!
                            </p>
                            <button
                              onClick={() => navigate('/pricing')}
                              className="inline-flex items-center px-4 py-2 text-sm font-semibold rounded-lg text-white bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 transition-all transform hover:scale-105"
                            >
                              Upgrade Now
                              <ArrowRightIcon className="ml-2 h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Show progress bar only for running jobs with known total */}
                    {isRunning && job.posts_total > 0 && (
                      <div className="mb-4">
                        <div className="bg-white/10 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-pink-500 to-purple-600 h-full transition-all duration-500"
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-400 mt-2 text-center">{progress}% complete</p>
                      </div>
                    )}
                    
                    <div className="text-sm text-gray-400">
                      <p>Started: {formatDate(job.started_at)}</p>
                      {job.completed_at && (
                        <p>Completed: {formatDate(job.completed_at)}</p>
                      )}
                    </div>
                  </div>
                );
                })()}
              </div>
            )}
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-6 mt-8">
          <div className="flex flex-wrap gap-2">
            {['all', 'pending', 'processing', 'completed', 'failed'].map((status) => (
              <button
                key={status}
                onClick={() => handleFilterChange(status)}
                className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-200 ${
                  filter === status
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                    : 'bg-white/10 text-gray-300 hover:bg-white/20 border border-white/20'
                }`}
              >
                {t(`backups.filters.${status}`)}
              </button>
            ))}
          </div>
          
          {/* Retry All Button - Show only if there are failed backups */}
          {backups?.some(backup => backup.status === 'failed' && backup.instagram_post_id) && (
            <button
              onClick={handleRetryAllFailed}
              disabled={retryingPosts.size > 0}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-200"
            >
              <ArrowPathIcon className={`h-4 w-4 mr-2 ${retryingPosts.size > 0 ? 'animate-spin' : ''}`} />
              {t('backups.retryAllFailed')}
            </button>
          )}
        </div>

        {/* Account Selector for Pro/Agency users */}
        {['pro', 'agency'].includes(user?.subscription_tier) && accountsData?.accounts?.length > 1 && (
          <div className="mb-6">
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-4 border border-white/20">
              <div className="flex items-center space-x-4">
                <UserCircleIcon className="h-5 w-5 text-gray-400" />
                <span className="text-sm text-gray-300">{t('backups.filterByAccount')}</span>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => handleAccountChange(null)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                      selectedAccountId === null
                        ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                        : 'bg-white/10 text-gray-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    {t('backups.allAccounts')}
                  </button>
                  {accountsData.accounts.map(account => (
                    <button
                      key={account.id}
                      onClick={() => handleAccountChange(account.id)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-all ${
                        selectedAccountId === account.id
                          ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                          : 'bg-white/10 text-gray-300 hover:bg-white/20 border border-white/20'
                      }`}
                    >
                      @{account.username}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Backups List */}
        <div className="bg-white/10 backdrop-blur-lg rounded-3xl border border-white/20 shadow-2xl overflow-hidden">
          <div className="px-8 py-6 border-b border-white/10">
            <h3 className="text-xl font-semibold text-white">
              {t('backups.postBackups', { count: totalBackups || 0 })}
            </h3>
          </div>
          <div className="p-0">
            {backups && backups.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-white/5">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        {t('backups.table.post')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        {t('backups.table.status')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        {t('backups.table.size')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        {t('backups.table.backupDate')}
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                        {t('backups.table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {backups.map((backup) => (
                      <tr key={backup.id} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-12 w-12 relative group cursor-pointer" 
                                 onClick={() => handleMediaClick(backup.instagram_post)}>
                              {backup.instagram_post?.thumbnail_url ? (
                                <>
                                  <img
                                    className="h-12 w-12 rounded-xl object-cover shadow-lg group-hover:opacity-75 transition-opacity"
                                    src={backup.instagram_post.thumbnail_url}
                                    alt="Post thumbnail"
                                  />
                                  {isVideo(backup.instagram_post?.media_type) && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <PlayIcon className="h-6 w-6 text-white drop-shadow-lg" />
                                    </div>
                                  )}
                                </>
                              ) : (
                                <div className="h-12 w-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                                  <PhotoIcon className="h-6 w-6 text-white" />
                                </div>
                              )}
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-white">
                                {backup.instagram_post?.media_type || 'Unknown'}
                              </div>
                              <div className="text-sm text-gray-300 max-w-xs truncate">
                                {backup.instagram_post?.caption || 'No caption'}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(backup.status)}
                            <span className={`ml-2 ${getStatusBadge(backup.status)}`}>
                              {backup.status}
                            </span>
                          </div>
                          {backup.error_message && !backup.error_message.toLowerCase().includes('skipped duplicate') && (
                            <p className={`text-xs mt-1 max-w-xs truncate ${
                              backup.error_message.toLowerCase().includes('carousel') && 
                              backup.error_message.toLowerCase().includes('backed up')
                                ? 'text-green-400' 
                                : 'text-red-400'
                            }`}>
                              {backup.error_message}
                            </p>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                          {formatBytes(backup.backup_size_bytes)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {formatDate(backup.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            {backup.google_drive_file_id && backup.status === 'completed' && (
                              <button
                                onClick={(e) => handleViewBackup(backup, e)}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-lg hover:shadow-xl transition-all duration-200"
                                title={isCarouselBackup(backup) ? 'View carousel folder' : 'View file'}
                              >
                                {isCarouselBackup(backup) ? (
                                  <FolderIcon className="h-3 w-3 mr-1" />
                                ) : (
                                  <PhotoIcon className="h-3 w-3 mr-1" />
                                )}
                                {isCarouselBackup(backup) ? t('backups.viewFolder') : t('backups.view')}
                              </button>
                            )}
                            {backup.status === 'failed' && backup.instagram_post_id && (
                              <button
                                onClick={() => handleRetryBackup(backup.instagram_post_id)}
                                disabled={retryingPosts.has(backup.instagram_post_id)}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-xs font-medium rounded-lg text-white bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transition-all duration-200"
                              >
                                {retryingPosts.has(backup.instagram_post_id) ? (
                                  <ArrowPathIcon className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <PlayIcon className="h-3 w-3 mr-1" />
                                )}
                                {t('backups.retry')}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12">
                <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-white">{t('backups.noBackupsFound')}</h3>
                <p className="mt-1 text-sm text-gray-400">
                  {filter === 'all' 
                    ? t('backups.noBackupsDescription')
                    : t('backups.noBackupsWithStatus', { status: t(`backups.filters.${filter}`) })
                  }
                </p>
              </div>
            )}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="px-8 py-4 border-t border-white/10 flex items-center justify-between">
              <div className="text-sm text-gray-400">
                {t('backups.pagination.showing', { 
                  start: ((currentPage - 1) * ITEMS_PER_PAGE) + 1, 
                  end: Math.min(currentPage * ITEMS_PER_PAGE, totalBackups), 
                  total: totalBackups 
                })}
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-white/10 hover:bg-white/20 border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  <ChevronLeftIcon className="h-4 w-4 mr-1" />
                  {t('common.previous')}
                </button>
                <div className="flex items-center space-x-1">
                  {/* Page numbers */}
                  {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = idx + 1;
                    } else if (currentPage <= 3) {
                      pageNum = idx + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + idx;
                    } else {
                      pageNum = currentPage - 2 + idx;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all ${
                          currentPage === pageNum
                            ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white shadow-lg'
                            : 'text-gray-300 hover:bg-white/20 border border-white/20'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg text-white bg-white/10 hover:bg-white/20 border border-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {t('common.next')}
                  <ChevronRightIcon className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm" onClick={() => setSelectedVideo(null)}>
          <div className="relative max-w-4xl max-h-[90vh] w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setSelectedVideo(null)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-10"
            >
              <XMarkIcon className="h-8 w-8" />
            </button>
            
            <div className="bg-black rounded-xl overflow-hidden shadow-2xl">
              <video
                src={selectedVideo.media_url}
                controls
                autoPlay
                className="w-full h-auto max-h-[80vh]"
                poster={selectedVideo.thumbnail_url}
              >
                Your browser does not support the video tag.
              </video>
              
              <div className="p-4 bg-gray-900 text-white">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-300">
                    {formatDate(selectedVideo.timestamp)}
                  </span>
                  {selectedVideo.like_count > 0 && (
                    <span className="text-sm text-gray-300">
                      ❤️ {selectedVideo.like_count}
                    </span>
                  )}
                </div>
                {selectedVideo.caption && (
                  <p className="text-sm text-gray-200 line-clamp-3">
                    {selectedVideo.caption}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BackupProgress; 